import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { AlertCircle, Clock, DollarSign, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationForm = ({ standalone = false }) => {
  const [submitting, setSubmitting] = useState(false);

  // Form state for Contract/As-Needed role only
  const [formData, setFormData] = useState({
    role: 'Contract / As-Needed Assistant',
    full_name: '',
    email: '',
    phone: '',
    location: '',
    availability: '',
    skills: [],
    experience: '',
    hourly_rate: '',
    portfolio: '',
    notes: '',
    source: 'applications-page'
  });

  const skillsOptions = [
    'Calendar & scheduling',
    'Email management',
    'Research & data entry',
    'Travel coordination',
    'Vendor communication',
    'Customer service',
    'Social media',
    'Document creation'
  ];

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/applications`, formData);
      toast.success('Application submitted successfully!');
      // Redirect to submitted page
      window.location.href = '/submitted-application';
      // Reset form
      setFormData({
        role: 'Contract / As-Needed Assistant',
        full_name: '',
        email: '',
        phone: '',
        location: '',
        availability: '',
        skills: [],
        experience: '',
        hourly_rate: '',
        portfolio: '',
        notes: '',
        source: 'applications-page'
      });
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
      console.error('Application submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={standalone ? "py-12 px-4 sm:px-6 lg:px-8" : ""}>
      <div className="max-w-2xl mx-auto">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Currently accepting Contract/As-Needed applications only</p>
            <p className="text-xs text-amber-600 mt-1">Full-time Executive Assistant, Personal/Admin Assistant, and Marketing Specialist positions are currently filled.</p>
          </div>
        </div>

        <Card className="border-2 border-green-100">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-lg">
            <div className="flex items-center gap-2 text-green-600 text-sm font-medium mb-2">
              <Clock className="w-4 h-4" />
              NOW HIRING
            </div>
            <CardTitle className="text-xl text-gray-900">Contract / As-Needed Assistant Application</CardTitle>
            <CardDescription className="text-gray-600">
              Flexible project-based work with competitive hourly pay. Choose your projects and set your own hours.
            </CardDescription>
            
            {/* Role Benefits */}
            <div className="flex flex-wrap gap-3 mt-4">
              <div className="flex items-center gap-1.5 text-xs bg-white px-3 py-1.5 rounded-full border border-gray-200">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                <span className="text-gray-700">Flexible schedule</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs bg-white px-3 py-1.5 rounded-full border border-gray-200">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                <span className="text-gray-700">Choose your projects</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs bg-white px-3 py-1.5 rounded-full border border-gray-200">
                <DollarSign className="w-3.5 h-3.5 text-green-500" />
                <span className="text-gray-700">Competitive hourly rate</span>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="role" className="text-gray-700">Role Applying For</Label>
                <Input id="role" value={formData.role} disabled className="bg-gray-50 text-gray-600 mt-1.5" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name" className="text-gray-700">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Smith"
                    className="mt-1.5"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-700">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="mt-1.5"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-gray-700">Phone / WhatsApp</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="location" className="text-gray-700">Location / Time Zone *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g. Atlanta, GA (EST)"
                    className="mt-1.5"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="availability" className="text-gray-700">Weekly Availability *</Label>
                  <Select 
                    value={formData.availability} 
                    onValueChange={(value) => setFormData({ ...formData, availability: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select hours per week" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5 hrs/week">1-5 hrs/week</SelectItem>
                      <SelectItem value="5-10 hrs/week">5-10 hrs/week</SelectItem>
                      <SelectItem value="10-20 hrs/week">10-20 hrs/week</SelectItem>
                      <SelectItem value="20+ hrs/week">20+ hrs/week</SelectItem>
                      <SelectItem value="Flexible / Project-based">Flexible / Project-based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="hourly_rate" className="text-gray-700">Expected Hourly Rate</Label>
                  <Input
                    id="hourly_rate"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    placeholder="e.g. $25-35/hr"
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-700">Skills & Tasks You Can Handle *</Label>
                <p className="text-xs text-gray-500 mb-2">Select all that apply</p>
                <div className="grid grid-cols-2 gap-2.5">
                  {skillsOptions.map((skill) => (
                    <div key={skill} className="flex items-center space-x-2">
                      <Checkbox
                        id={`skill-${skill}`}
                        checked={formData.skills.includes(skill)}
                        onCheckedChange={() => handleSkillToggle(skill)}
                      />
                      <label 
                        htmlFor={`skill-${skill}`} 
                        className="text-sm cursor-pointer text-gray-700 hover:text-gray-900"
                      >
                        {skill}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="experience" className="text-gray-700">Briefly describe your relevant experience *</Label>
                <Textarea
                  id="experience"
                  placeholder="Tell us about your previous assistant, admin, or related work experience..."
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  rows={3}
                  className="mt-1.5"
                  required
                />
              </div>

              <div>
                <Label htmlFor="portfolio" className="text-gray-700">Resume / LinkedIn / Portfolio Link</Label>
                <Input
                  id="portfolio"
                  placeholder="https://"
                  value={formData.portfolio}
                  onChange={(e) => setFormData({ ...formData, portfolio: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="notes" className="text-gray-700">Anything else we should know?</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="mt-1.5"
                  placeholder="Special skills, certifications, availability notes..."
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-3 text-base font-semibold"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Contract Application'}
              </Button>
              
              <p className="text-xs text-center text-gray-500 mt-2">
                We typically respond within 48 hours for qualified candidates.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationForm;
